import React, { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react';
import { Icon, ListItem, Loading } from '@startlibs/components';
import styled from 'styled-components';
import { useFormValue } from '@startlibs/form';
import { getColor, media } from '@startlibs/utils';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;

  label {
    display: inline-block;
    font-size: 14px;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #3C3C3C;
  }
`;

const Mandatory = styled.span`
  color: #c3282d;
  margin-left: 1px;
  vertical-align: top;
  line-height: 100%;
  margin-top: -1px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 3rem;
  padding: 0 0.7rem;
  border: 1px solid ${({ hasError }) => (hasError ? '#C3282D' : '#d2d2d2')};
  border-radius: 6px;
  background: white;
  cursor: text;
`;

const TagPill = styled.div`
  background-color: rgb(230, 230, 230);
  padding: 2px 2rem 4px 0.75rem;
  border-radius: 12px;
  font-size: 13px;
  position: relative;
  margin: 0.4rem 0.7rem 0 -0.25rem;
  white-space: nowrap;
  display: inline-block;
  cursor: default;
  line-height: normal;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: -7px;
`;

const ClearSearchButton = styled(Icon)`
  color: #787878;
  font-size: 14px;
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 20px;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  :hover {
    border-radius: 50%;
    background-color: #d2d2d2;
    color: ${getColor('alert')};
  }
`;

const InputField = styled.input`
  flex: 1;
  min-width: 80px;
  border: none;
  outline: none;
  font-size: 14px;
  color: #3c3c3c;
  background: transparent;
  height: 2.5rem;
  margin: 0.2rem 0.25rem;
  padding: 0;

  ::-webkit-input-placeholder { color: rgba(0, 0, 0, 0.3); }
  ::-moz-placeholder { color: rgba(0, 0, 0, 0.3); }
  :-moz-placeholder { color: rgba(0, 0, 0, 0.3); }
  :-ms-input-placeholder { color: rgba(0, 0, 0, 0.3); }
`;

const AutoCompleteMenu = styled.ul`
  position: absolute;
  background: white;
  border: 1px solid #d2d2d2;
  border-radius: 6px 6px 6px 6px;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  margin-top: 0px;
  z-index: 10;
  padding: 0.25rem 0;
  box-sizing: border-box;
`;

const AutoCompleteLi = styled(ListItem)`
  background-color: ${(props) => (props.isHighlighted ? getColor('main') : 'white')};
  padding: 11px;
  font-size: 13px;
  cursor: pointer;
  color: ${(props) => (props.isHighlighted ? 'white' : 'black')};

  &:not(:last-child) {
    border-bottom: 1px solid #e0e0e0;
  }
`;

const DropdownIconContainer = styled(Icon)`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 3rem;
  height: 3rem;
  top: 26px;
  ${media.mobile` height: 42px; `}
  ${Icon} {
    position: absolute;
    width: 2rem;
    height: 2rem;
    line-height: 2rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    text-align: center;
    cursor: pointer;
    color: ${getColor('gray90')};
    :hover, :active { background: rgba(0, 0, 0, 0.15); }
  }
`;

const BelowDescText = styled.div`
  color: rgba(0, 0, 0, 0.4);
  display: block;
  font-size: 12px;
  font-weight: 400;
  margin: 0.5rem 0 0;
`;

const InlineError = styled.div`
  color: #C3282D;
  font-size: 12px;
  margin-top: .5rem;
`;

export const TypeOrSelectTags = forwardRef(({
  label,
  path,
  placeholder,
  initOptions = [],
  tabIndex = 0,
  disabled = false,
  mandatory = false,
  hasErrors = false,
  isLoading = false,
  bellowDescText = '',
  autoFocus = false,
  /** plug your validator and form to surface errors */
  validation,      // (value) => { success?: string, errors?: string[] }
  form,
  showInputError = false,
  showCreateOption = false,
  ...props
}, ref) => {
  const [tags, setTags] = useFormValue(path);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(null);

  const [filteredOptions, setFilteredOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const hasError =
    (!!hasErrors &&
      ((mandatory && (!Array.isArray(tags) || tags.length === 0)) ||
        (Array.isArray(tags) &&
          tags.some((t) => typeof t === 'string' && t.length > 120)))) ||
    Boolean(inputError);

  const normalize = (s) => s.trim().replace(/\u200b/g, '');

  const commitTag = (raw) => {
    const candidateRaw = normalize(raw);
    if (candidateRaw === '') return false;

    let candidate = candidateRaw;
    if (typeof validation === 'function') {
      const res = validation(candidateRaw);
      if (res && Array.isArray(res.errors) && res.errors.length) {
        const msg = res.errors[0];
        setInputError(msg);
        try { form && form.setError && form.setError(path, msg); } catch (_) {}
        return false;
      }
      if (res && res.success) candidate = res.success;
    }

    const existing = Array.isArray(tags) ? tags : [];
    if (existing.includes(candidate)) {
      setInputError(null);
      try { form && form.clearErrors && form.clearErrors(); } catch (_) {}
      return false;
    }

    setTags([...existing, candidate]);
    setInputError(null);
    try { form && form.clearErrors && form.clearErrors(); } catch (_) {}
    return true;
  };

  const removeTag = (i) => {
    if (!Array.isArray(tags)) return;
    const next = [...tags];
    next.splice(i, 1);
    setTags(next);
  };

  const handleContainerClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const openDropdown = (typed = '') => {
    const lowerTyped = typed.toLowerCase();
    const baseOptions = initOptions
      .filter((opt) => !tags?.includes(opt))
      .filter((opt) => opt.toLowerCase().includes(lowerTyped));

    const exactMatch = initOptions.some(
      (opt) => opt.toLowerCase() === typed.toLowerCase()
    );

    let newList = [...baseOptions];

    // Only append the "Create option" row if allowed
    if (showCreateOption && typed.trim() !== '' && !exactMatch) {
      newList = [...newList, { isNew: true, label: typed }];
    }

    setFilteredOptions(newList);
    setHighlightedIndex(newList.length > 0 ? 0 : -1);
    setDropdownOpen(true);
    setIsKeyboardNavigating(true);
  };

  const openDropdownWithAllOptions = () => {
    const baseOptions = initOptions.filter((opt) => !tags?.includes(opt));
    const exactMatch = initOptions.some(
      (opt) => opt.toLowerCase() === inputValue.toLowerCase()
    );

    let newList = [...baseOptions];

    // Only prepend the "Create option" row if allowed
    if (showCreateOption && inputValue.trim() !== '' && !exactMatch) {
      newList = [{ isNew: true, label: inputValue }, ...newList];
    }

    setFilteredOptions(newList);
    setHighlightedIndex(newList.length > 0 ? 0 : -1);
    setDropdownOpen(true);
    setIsKeyboardNavigating(true);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setHighlightedIndex(-1);
    setHoveredIndex(null);
    setIsKeyboardNavigating(false);
  };

  // const handleSelectOption = (option) => {
  //   if (option.isNew) commitTag(option.label);
  //   else commitTag(option);
  //   setInputValue('');
  //   closeDropdown();
  // };

  const handleSelectOption = (option) => {
    const ok = option.isNew ? commitTag(option.label) : commitTag(option);
    if (ok) {
      setInputValue('');
      closeDropdown();
    } else {
      // keep input and dropdown so the user can fix the invalid entry
    }
  };

  const handleClickOutside = (e) => {
    const clickInsideInput = inputRef.current && inputRef.current.contains(e.target);
    const clickInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);

    if (!clickInsideInput && !clickInsideDropdown) {
      if (normalize(inputValue) !== '') {
        if (commitTag(inputValue)) setInputValue('');
      }
      closeDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, tags, validation]);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const lis = dropdownRef.current.querySelectorAll('li');
      const li = lis[highlightedIndex];
      if (li) li.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, filteredOptions]);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setInputValue(input);
    if (inputError) setInputError(null);

    if (input === '') closeDropdown();
    else openDropdown(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      const trimmed = normalize(inputValue);

      if (dropdownOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        e.preventDefault();
        handleSelectOption(filteredOptions[highlightedIndex]);
        return;
      }

      if (trimmed !== '') {
        e.preventDefault();
        const ok = commitTag(trimmed);
        if (ok) {
          setInputValue('');
          closeDropdown();
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdownOpen) openDropdown(inputValue);
      else {
        setIsKeyboardNavigating(true);
        setHoveredIndex(null);
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdownOpen) {
        setIsKeyboardNavigating(true);
        setHoveredIndex(null);
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (
      e.key === 'Backspace' &&
      inputValue === '' &&
      Array.isArray(tags) &&
      tags.length > 0
    ) {
      e.preventDefault();
      removeTag(tags.length - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setInputValue('');
      closeDropdown();
    }
  };

  const handlePaste = (e) => {
    const pastedRaw = e.clipboardData.getData('text');
    if (!pastedRaw) return;
    if (!pastedRaw.includes(',') && !pastedRaw.includes(';')) return;

    e.preventDefault();

    const parts = pastedRaw
      .split(/[,;]+/)
      .map((p) => normalize(p))
      .filter((p) => p.length > 0);

    let firstInvalid = null;
    const toAdd = [];

    parts.forEach((p) => {
      const res = typeof validation === 'function' ? validation(p) : { success: p };
      if (res && Array.isArray(res.errors) && res.errors.length) {
        if (!firstInvalid) firstInvalid = res.errors[0];
      } else if (res && res.success) {
        toAdd.push(res.success);
      }
    });

    const existing = Array.isArray(tags) ? tags : [];
    const merged = [...existing];

    if (inputValue) {
      const combined = normalize(inputValue + (toAdd[0] || ''));
      if (combined && !merged.includes(combined)) merged.push(combined);
    } else if (toAdd[0] && !merged.includes(toAdd[0])) {
      merged.push(toAdd[0]);
    }

    for (let i = 1; i < toAdd.length; i++) {
      const part = toAdd[i];
      if (!merged.includes(part)) merged.push(part);
    }

    setTags(merged);

    if (firstInvalid) {
      // show error but DO NOT wipe what the user typed
      setInputError(firstInvalid);
      try { form && form.setError && form.setError(path, firstInvalid); } catch (_) {}
      // keep inputValue as-is and keep dropdown open
    } else {
      setInputError(null);
      try { form && form.clearErrors && form.clearErrors(); } catch (_) {}
      setInputValue('');
      closeDropdown();
    }
  };

  const handleMouseEnter = (index) => {
    if (!isKeyboardNavigating) {
      setHighlightedIndex(-1);
    }
    setHoveredIndex(index);
  };

  const handleMouseMove = () => {
    if (isKeyboardNavigating) {
      setIsKeyboardNavigating(false);
      setHighlightedIndex(-1);
    }
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  useImperativeHandle(ref, () => ({
    addValue: (v) => commitTag(v),
    focus: () => inputRef.current && inputRef.current.focus(),
  }), [tags, validation, form, inputRef]);

  return (
    <Wrapper ref={ref}>
      <label>
        {label}
        {mandatory && <Mandatory>*</Mandatory>}
      </label>

      <TagsContainer hasError={!!hasError} onClick={handleContainerClick} data-testid="tags-container">
        {Array.isArray(tags) && tags.map((tag, idx) => (
          <TagPill key={`${tag}-${idx}`}>
            {tag}
            <ClearSearchButton icon="x" onClick={() => removeTag(idx)} />
          </TagPill>
        ))}

        <InputField
          type="text"
          value={inputValue}
          onClick={openDropdownWithAllOptions}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => { closeDropdown(); }}
          onPaste={handlePaste}
          placeholder={Array.isArray(tags) && tags.length > 0 ? '' : placeholder}
          ref={inputRef}
          tabIndex={tabIndex}
          disabled={disabled}
          autoFocus={autoFocus}
          {...props}
        />

        {isLoading
          ? <Loading size={18} borderWidth={3} />
          : (initOptions && initOptions.length > 0 && (
              <DropdownIconContainer onClick={openDropdownWithAllOptions}>
                <Icon icon="arrow-down" />
              </DropdownIconContainer>
            ))
        }
      </TagsContainer>

      {dropdownOpen && filteredOptions.length > 0 && (
        <AutoCompleteMenu ref={dropdownRef}>
          {filteredOptions.map((option, index) => {
            const isHighlighted = index === (isKeyboardNavigating ? highlightedIndex : hoveredIndex);

            if (showCreateOption && option && option.isNew) {
              return (
                <AutoCompleteLi
                  key={`new-${option.label}`}
                  isHighlighted={isHighlighted}
                  onMouseDown={(e) => { e.preventDefault(); handleSelectOption(option); }}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{ color: isHighlighted ? 'white' : '#008bd2', fontWeight: 'normal' }}
                >
                  {`Create option ${option.label}`}
                </AutoCompleteLi>
              );
            }

            return (
              <AutoCompleteLi
                key={option}
                isHighlighted={isHighlighted}
                onMouseDown={(e) => { e.preventDefault(); handleSelectOption(option); }}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                {option}
              </AutoCompleteLi>
            );
          })}
        </AutoCompleteMenu>
      )}

      {showInputError && inputError && <InlineError>{inputError}</InlineError>}
      {bellowDescText && bellowDescText.length > 0 && (
        <BelowDescText>{bellowDescText}</BelowDescText>
      )}
    </Wrapper>
  );
});

TypeOrSelectTags.displayName = 'TypeOrSelectTags';
export default TypeOrSelectTags;
